// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { CameraSource } from "@capacitor/camera";
import {
  BottomSheet,
  BottomSheetIllustrationContent,
  PageFooter,
  useModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { useUpdateProfilePicture } from "@src/appV2/Agents/api/useUpdateProfilePicture";
import {
  resetAgentProfilePictureQuery,
  useWorkerProfilePicture,
} from "@src/appV2/Agents/api/useWorkerProfilePicture";
import { useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS, logError } from "@src/appV2/lib/analytics";
import { type CameraPermissionDeniedError } from "@src/appV2/lib/Media/CameraPermissionDeniedError";
import { type UploadFileData } from "@src/appV2/lib/Media/types";
import { useBrowserOnFileChange } from "@src/appV2/lib/Media/useBrowserOnFileChange";
import { useMobileOnFileSelect } from "@src/appV2/lib/Media/useMobileOnFileSelect";
import { useTakePhoto } from "@src/appV2/lib/Media/useTakePhoto";
import { isCapacitorPlatform } from "@src/appV2/lib/utils/capacitor/platformUtils";
import { openUrlInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import { BottomSheetHeader } from "../../components/BottomSheetHeader";
import { Button } from "../../components/Button";
import { DialogFooter } from "../../components/DialogFooter";
import { FileUploaderBottomSheet } from "../../components/FileUploaderBottomSheet";
import { StickyScrollPageHeader } from "../../components/StickyScrollPageHeader";
import { ImageCropperModal } from "../../Profile/UpdateProfileImage/ImageCropperModal";
import { PageContent } from "../../WorkerShifts/components/PageContent";
import { BadgeSmithCheckoutContent } from "./BadgeSmithCheckoutContent";

interface BadgeSmithCheckoutProps {
  checkoutUrl: string;
  onBack: () => void;
}

export function BadgeSmithCheckout(props: BadgeSmithCheckoutProps) {
  const { checkoutUrl, onBack } = props;

  const worker = useDefinedWorker();
  const queryClient = useQueryClient();
  const { showErrorToast } = useToast();
  const inputFileRef = useRef<HTMLInputElement>(null);

  const updatePhotoModalState = useModalState();
  const fileUploaderModalState = useModalState();
  const imageCropperModalState = useModalState();

  const {
    data: profilePicture,
    isLoading: isLoadingProfilePicture,
    isError: isErrorProfilePicture,
    refetch: refetchProfilePicture,
  } = useWorkerProfilePicture(worker.userId);

  const handleOpenCheckoutUrl = useCallback(async () => {
    try {
      await openUrlInBrowser(checkoutUrl, "badge-smith-checkout");
    } catch (error: unknown) {
      showErrorToast("Failed to open checkout. Please try again.");
      logError(APP_V2_APP_EVENTS.BADGE_SMITH_CHECKOUT_OPEN_FAILURE, {
        error,
        metadata: { workerId: worker.userId },
      });
    }
  }, [checkoutUrl, showErrorToast, worker.userId]);

  const { mutateAsync: updateProfilePicture, isLoading: isUploading } = useUpdateProfilePicture({
    onSuccess: async () => {
      resetAgentProfilePictureQuery(queryClient, { workerId: worker.userId });
      await handleOpenCheckoutUrl();
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.WORK_BADGE_PHOTO_UPLOAD_FAILURE,
      userErrorMessage: "Failed to update profile photo. Please try again.",
    },
  });

  const hasProfilePicture = isDefined(profilePicture?.distribution_url);

  const [selectedImage, setSelectedImage] = useState<{
    dataUrl: string;
    blob: Blob;
  }>();

  const handlePhotoSelected = useCallback(
    (photo: UploadFileData) => {
      setSelectedImage({
        dataUrl: photo.dataUrlBase64,
        blob: photo.blob,
      });
      fileUploaderModalState.closeModal();
      imageCropperModalState.openModal();
    },
    [fileUploaderModalState, imageCropperModalState]
  );

  const handleError = useCallback(
    (error: Error | CameraPermissionDeniedError) => {
      showErrorToast("Failed to select photo. Please try again.");
      logError(APP_V2_USER_EVENTS.WORK_BADGE_PHOTO_SELECTION_FAILURE, {
        error,
        metadata: { workerId: worker.userId },
      });
    },
    [showErrorToast, worker.userId]
  );

  const handleTakePhoto = useTakePhoto({
    source: CameraSource.Camera,
    onSuccess: handlePhotoSelected,
    onError: handleError,
  });

  const handleSelectPhotoFromCameraRoll = useTakePhoto({
    source: CameraSource.Photos,
    onSuccess: handlePhotoSelected,
    onError: handleError,
  });

  const handleSelectFile = useMobileOnFileSelect({
    onSuccess: handlePhotoSelected,
    onError: handleError,
  });

  const browserOnFileChange = useBrowserOnFileChange({
    onSuccess: handlePhotoSelected,
    onError: handleError,
  });

  const handleBrowserSelectFile = useCallback(() => {
    inputFileRef.current?.click();
  }, []);

  const handleOpenFileSelector = useCallback(() => {
    if (isCapacitorPlatform()) {
      fileUploaderModalState.openModal();
    } else {
      handleBrowserSelectFile();
    }
  }, [fileUploaderModalState, handleBrowserSelectFile]);

  const handleUploadPhoto = () => {
    updatePhotoModalState.closeModal();
    handleOpenFileSelector();
  };

  const handleCropComplete = useCallback(
    async (croppedBlob: Blob) => {
      imageCropperModalState.closeModal();

      const file = new File([croppedBlob], "profile-image.jpg", {
        type: "image/jpeg",
      });

      try {
        await updateProfilePicture({
          file,
          workerId: worker.userId,
        });
        setSelectedImage(undefined);
      } catch {
        // onError on the mutation handles user-facing feedback and logging.
      }
    },
    [imageCropperModalState, updateProfilePicture, worker.userId]
  );

  const handleContinueToCheckout = () => {
    updatePhotoModalState.closeModal();
    void handleOpenCheckoutUrl();
  };

  const handleOrderBadge = () => {
    if (hasProfilePicture) {
      updatePhotoModalState.openModal();
    } else {
      handleOpenFileSelector();
    }
  };

  return (
    <>
      <StickyScrollPageHeader
        title="Order Your Badge"
        headerVariant="tertiary"
        onClickBack={onBack}
      />

      <PageContent>
        <BadgeSmithCheckoutContent
          profilePictureUrl={profilePicture?.distribution_url}
          isLoading={isLoadingProfilePicture}
          isError={isErrorProfilePicture}
          onRetry={() => void refetchProfilePicture()}
        />
      </PageContent>

      <PageFooter>
        <Button
          fullWidth
          size="large"
          disabled={isLoadingProfilePicture || isErrorProfilePicture || isUploading}
          onClick={handleOrderBadge}
        >
          {hasProfilePicture ? "Order your badge" : "Add a profile photo"}
        </Button>
      </PageFooter>

      {/* Bottom sheet for users who already have a profile photo */}
      <BottomSheet
        modalState={updatePhotoModalState}
        header={<BottomSheetHeader onClose={updatePhotoModalState.closeModal} />}
        footer={
          <DialogFooter orientation="vertical">
            <Button
              fullWidth
              variant="contained"
              size="large"
              disabled={isUploading}
              onClick={handleContinueToCheckout}
            >
              Continue with current photo
            </Button>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              disabled={isUploading}
              onClick={handleUploadPhoto}
            >
              Update photo first
            </Button>
          </DialogFooter>
        }
      >
        <BottomSheetIllustrationContent
          illustrationType="request-sent"
          title="Your profile photo will be used"
          description="Your current profile photo will appear on your printed badge. Would you like to update it first?"
        />
      </BottomSheet>

      {/* File uploader bottom sheet for taking/selecting photos */}
      <FileUploaderBottomSheet
        modalState={fileUploaderModalState}
        onTakePhoto={handleTakePhoto}
        onSelectPhotoFromCameraRoll={handleSelectPhotoFromCameraRoll}
        onSelectFile={handleSelectFile}
      />

      {/* Image cropper */}
      {isDefined(selectedImage) && (
        <ImageCropperModal
          modalState={imageCropperModalState}
          imageDataUrl={selectedImage.dataUrl}
          isUploading={isUploading}
          onCropComplete={handleCropComplete}
          onCancel={() => {
            imageCropperModalState.closeModal();
            setSelectedImage(undefined);
          }}
        />
      )}

      {/* Hidden file input for browser */}
      <input
        ref={inputFileRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(event) => {
          void browserOnFileChange(event);
        }}
      />
    </>
  );
}
